// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import ChevronLeft from "@west/XDL/Icons/ChevronLeft";
import ChevronRight from "@west/XDL/Icons/ChevronRight";
import Close from "@west/XDL/Icons/Close";
import IconCalendar from "@west/XDL/Icons/IconCalendar";
import { xBaseTheme } from "@west/XDL/types";
import { color } from "@west/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...DatePickerPresentationPropsDefault,
        mergeCss: true,
        dateTimePickerProps: {
            showNeighboringMonth: true,
            nextLabel: React.createElement(ChevronRight),
            prevLabel: React.createElement(ChevronLeft),
        } as any,
        clearIconProps: {
            color: "common.ui4",
            src: Close,
            size: 12,
            css: css`
                &&& {
                    margin-right: 5px;
                    padding: 0;
                }
                &:hover {
                    color: ${color("danger")};
                }
            `,
        },
        calendarIconProps: {
            color: "common.ui4",
            src: IconCalendar,
            size: 14,
            css: css`
                &&& {
                    padding: 0;
                }
                &:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YY",
            hourPlaceholder: "hr",
            minutePlaceholder: "min",
        },
        cssOverrides: {
            datePicker: css`
                button:focus {
                    /* outline is added on span for border reasons */
                    outline: none;
                    span {
                        outline: none;
                    }
                }

                &&& .react-datetime-picker {
                    height: 36px;

                    :not(:disabled) {
                        .react-datetime-picker__button:hover > ${IconWrapper} {
                            color: ${color("common.ui8")};
                        }
                        .react-datetime-picker__clear-button:hover > ${IconWrapper} {
                            color: ${color("common.ui8")};
                        }
                        .react-datetime-picker__button:focus > ${IconWrapper} {
                            color: ${color("common.ui9")};
                            outline: none;
                        }
                        .react-datetime-picker__clear-button:focus > ${IconWrapper} {
                            color: ${color("common.ui9")};
                            outline: none;
                        }
                    }

                    &:not(:disabled),
                    &:focus-within {
                        padding: 0;
                        outline: none;

                        .react-datetime-picker__inputGroup {
                            padding-left: 9px;
                        }

                        .react-datetime-picker__button {
                            padding-left: 0;
                            outline: none;
                        }

                        .react-datetime-picker__clear-button {
                            margin-left: 20px;
                            outline: none;
                        }
                    }
                }
                .react-calendar {
                    width: 280px;
                    box-shadow: none;
                    margin-top: 0;
                    outline: ${getColor("common.border")} solid 2px;
                    outline-offset: -1px;
                    padding: 15px 20px 20px 20px;
                    button {
                        font-family: inherit;
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui3")};
                            }
                        }
                    }

                    .react-calendar__navigation {
                        margin-bottom: 15px;

                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            min-width: 0;
                            padding: 0;
                            width: auto;
                            color: ${getColor("primary")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("primary")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }

                            svg {
                                width: 8px;
                                height: 12px;
                            }
                        }

                        &__label {
                            font-size: 16px;
                            font-weight: bold;
                            line-height: 18px;

                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }
                        }
                    }

                    &__month-view {
                        &__weekdays {
                            font-size: 10px;
                            font-weight: normal;
                            color: ${getColor("text")};
                            letter-spacing: 0.4px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                        }

                        &__days {
                            &__day {
                                color: ${getColor("secondary")};
                                border-radius: 0;
                                font-weight: normal;
                                font-size: 12px;
                                height: 29px;
                                width: 29px;
                                padding: 0;

                                &.react-calendar__tile--now {
                                    position: relative;
                                    &:after {
                                        content: "";
                                        position: absolute;
                                        bottom: 3px;
                                        left: 50%;
                                        transform: translateX(-50%);
                                        color: ${getColor("primary")};
                                        border: 2px solid ${getColor("primary")};
                                        border-radius: 50%;
                                        font-size: 0;
                                    }
                                }

                                &:disabled,
                                &--neighboringMonth {
                                    color: ${getColor("common.border")};
                                }
                            }
                        }
                    }

                    &__year-view,
                    &__decade-view,
                    &__century-view {
                        .react-calendar__tile {
                            color: ${getColor("secondary")};
                            padding: 5px 0;
                            font-weight: normal;
                            font-size: 12px;
                            &:disabled {
                                color: ${getColor("common.border")};
                            }
                        }
                    }

                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            &:after {
                                content: none;
                            }
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
